import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import { keyCode } from '@bingads-webui/keycode';
import { WithArrowKeys } from '@bingads-webui-react/hoc-with-arrow-keys';
import { PillItem } from './pill-item';

class RawPillControl extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    id: PropTypes.string,
    defaultSelectedIndex: PropTypes.number,
    onItemChange: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
      disabled: PropTypes.bool,
      labelString: PropTypes.string,
      href: PropTypes.string.isRequired,
      ariaDescription: PropTypes.string,
    })).isRequired,
    observable: PropTypes.shape({
      subscribe: PropTypes.func,
    }),
  }

  static defaultProps = {
    className: 'pill-control',
    id: null,
    defaultSelectedIndex: -1,
    onItemChange: null,
    observable: _.noop,
  }

  constructor(props) {
    super(props);
    this.id = this.props.id ? this.props.id : _.uniqueId('pill-');

    const observer = {
      next: (data) => {
        switch (data) {
          case keyCode.ArrowLeft:
            this.setState((preState, prevProps) => ({
              focusedIndex: this.getNextFocusedItemIndex(preState.focusedIndex, prevProps, false),
            }));
            break;
          case keyCode.ArrowRight:
            this.setState((preState, prevProps) => ({
              focusedIndex: this.getNextFocusedItemIndex(preState.focusedIndex, prevProps, true),
            }));
            break;
          default:
            break;
        }
      },
    };

    props.observable.subscribe(observer);

    this.state = { focusedIndex: -1 };
  }

  onBlur = () => {
    this.setState({ focusedIndex: -1 });
  }

  getNextFocusedItemIndex = (previousIndex, props, isNext) => {
    const maxIndex = props.items.length - 1;

    let newIndex;

    if (previousIndex < 0) {
      newIndex = 0;
    } else if (previousIndex > maxIndex) {
      newIndex = maxIndex;
    } else if (isNext && previousIndex === maxIndex) {
      newIndex = 0;
    } else if (!isNext && previousIndex === 0) {
      newIndex = maxIndex;
    } else {
      newIndex = isNext ? previousIndex + 1 : previousIndex - 1;
    }

    return newIndex;
  }

  focusItemCallback = (index) => {
    this.setState({ focusedIndex: index });
  }

  handleOnClick = (e, { name, value, index }) => {
    e.preventDefault();
    if (this.props.onItemChange) {
      this.props.onItemChange({ name, value, index });
    }
  }

  render() {
    const items = _.map(this.props.items, (item, index) => {
      const {
        name,
        value,
        disabled,
        href,
      } = item;
      const selected = index === this.props.defaultSelectedIndex;
      let className = selected ? 'active' : null;
      className = disabled ? 'disabled' : className;
      /* eslint-disable no-script-url */
      const url = selected || disabled || !href ? '#' : href;
      const hrefParam = { href: url };
      let onClick = !selected && !disabled && this.props.onItemChange
        ? e => this.handleOnClick(e, { name, value, index }) : null;
      // need to prevent default behavior of a tag when the url set to empty.
      if (onClick == null && url === '#') {
        onClick = e => e.preventDefault();
      }
      const uniqueId = `${this.id}-${index}`;

      return (
        <PillItem
          key={uniqueId}
          ariaDescribedby={uniqueId}
          item={item}
          onClick={onClick}
          index={index}
          className={className}
          hrefParam={hrefParam}
          isSelected={selected}
          isFocused={index === this.state.focusedIndex}
          focusItemCallback={this.focusItemCallback}
        />
      );
    });

    return (
      <ul
        className={classnames('nav nav-pills', this.props.className)}
        role="tablist"
        onBlur={this.onBlur}
      >
        {items}
      </ul>
    );
  }
}

export const PillControl = WithArrowKeys(RawPillControl);
